import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FaUserPlus, FaSync, FaTrashAlt, FaCopy, FaUsers } from "react-icons/fa";

// Admin panel for managing volunteer invite codes for an event.
// Wired to the existing backend:
//   GET    /api/v2/admin/volunteer/invites?event_id=
//   POST   /api/v2/admin/volunteer/invites        { event_id, name }
//   DELETE /api/v2/admin/volunteer/invites?code=
// Volunteers redeem the code (V-XXXXXX) in the Volunteer app to be bound to
// this event and check participants in via QR.
const VolunteerInvites = ({ eventId }) => {
  const apiDomain = process.env.REACT_APP_API_DOMAIN;
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const fetchInvites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${apiDomain}/api/v2/admin/volunteer/invites`, {
        params: { event_id: eventId, t: Date.now() },
      });
      const data = res.data;
      setInvites(Array.isArray(data) ? data : data?.invites || data?.data || []);
    } catch (e) {
      setError("Failed to load volunteer codes.");
    } finally {
      setLoading(false);
    }
  }, [apiDomain, eventId]);

  useEffect(() => {
    if (eventId) fetchInvites();
  }, [eventId, fetchInvites]);

  const createInvite = async () => {
    if (!name.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await axios.post(`${apiDomain}/api/v2/admin/volunteer/invites`, {
        event_id: String(eventId),
        name: name.trim(),
      });
      setName("");
      await fetchInvites();
    } catch (e) {
      setError("Failed to create volunteer code.");
    } finally {
      setCreating(false);
    }
  };

  const revokeInvite = async (code) => {
    if (!window.confirm(`Revoke volunteer code ${code}? The volunteer will lose access.`)) return;
    try {
      await axios.delete(`${apiDomain}/api/v2/admin/volunteer/invites`, {
        params: { code },
      });
      await fetchInvites();
    } catch (e) {
      setError("Failed to revoke code.");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
  };

  const statusBadge = (inv) => {
    if (inv.revoked_at || inv.revoked) return <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Revoked</span>;
    if (inv.used_at || inv.used_by_device) return <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">In use</span>;
    return <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">Active</span>;
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <FaUsers className="text-blue-500" /> Volunteer Invite Codes
        </h3>
        <button onClick={fetchInvites} title="Refresh"
          className="text-gray-500 hover:text-blue-600 p-2">
          <FaSync className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Create a code for each volunteer. They enter it in the Volunteer app to be
        bound to this event and check participants in via QR.
      </p>

      {/* Create */}
      <div className="flex gap-2 mb-4">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && createInvite()}
          placeholder="Volunteer name"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500"
        />
        <button
          onClick={createInvite}
          disabled={creating || !name.trim()}
          className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm"
        >
          <FaUserPlus /> {creating ? "Creating…" : "Create code"}
        </button>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {/* List */}
      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : invites.length === 0 ? (
        <p className="text-sm text-gray-400 italic">No volunteer codes yet for this event.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Code</th>
                <th className="py-2">Volunteer</th>
                <th className="py-2">Status</th>
                <th className="py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <tr key={inv.code} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-2 font-mono font-semibold text-gray-800">
                    <button onClick={() => copyCode(inv.code)} title="Copy"
                      className="inline-flex items-center gap-1 hover:text-blue-600">
                      {inv.code} <FaCopy size={11} className="text-gray-400" />
                    </button>
                  </td>
                  <td className="py-2 text-gray-700">{inv.name || "—"}</td>
                  <td className="py-2">{statusBadge(inv)}</td>
                  <td className="py-2 text-right">
                    {!(inv.revoked_at || inv.revoked) && (
                      <button onClick={() => revokeInvite(inv.code)}
                        className="text-red-500 hover:text-red-700 inline-flex items-center gap-1 text-xs">
                        <FaTrashAlt size={11} /> Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VolunteerInvites;
